import { ColDef, ColGroupDef, ColumnState } from "ag-grid-community";
import { EQM2060_R01Dto } from "../Dto_EQM2060_R01";
import { getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { aggridHeaderClass } from "src/components/common/common";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { formatGridNumber2 } from "src/utils/number/numberUtils";
import { formatDate } from "src/utils/string/stringUtil";
import {
	getDayOfWeekIndexFromDateString,
	getDayNameFromDateString,
} from "src/utils/date/dateUtils";

/**
 * 헤더 이름 매핑
 */
export const headerNames: Record<string, string> = {
	rowIndex: "",
	inspectionDt: "일자",
	dayOfWeek: "요일",
	industrialWater: '산업용수',
	domesticWater: '생활용수',
	electricalEnergy01: '옥상1',
	electricalEnergy02: '옥상2',
	electricalEnergy03: '옥상3',
	remark: '비고'
};

/**
 * 기본 컬럼 정의 생성 함수
 */
export const createBaseColumnDefs = (
	cellDateComparator: (valueA: Date, valueB: Date) => number
): (ColDef<EQM2060_R01Dto> | ColGroupDef<EQM2060_R01Dto>)[] => {
	return [
		// ...getDefaultRowIndexColumnConfig(renderIndexCell),
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '일자',
			colId: 'inspectionDt',
			field: 'inspectionDt',
			width: 100,
			valueFormatter: (params: any) => {
				if (!params.data?.inspectionDt) return '';
				return formatDate(new Date(params.data.inspectionDt));
			},
			comparator: cellDateComparator,
			editable: false,
		},
		{
			headerName: '요일',
			colId: 'dayOfWeek',
			width: 50,
			valueGetter: (params: any) => getDayNameFromDateString(params.data?.inspectionDt),
			cellClassRules: {
				'eqm2050-sunday-cell': (params: any) => getDayOfWeekIndexFromDateString(params.data?.inspectionDt) === 0,
				'eqm2050-saturday-cell': (params: any) => getDayOfWeekIndexFromDateString(params.data?.inspectionDt) === 6,
				'text-align-center': (params: any) => true,
			},
			editable: false,
		},
		{
			headerName: '니켈 1-1호기',
			headerClass: 'common-group-header',
			children: [
				{
					headerName: '교환여부',
					colId: 'nickel101',
					field: 'nickel101',
					width: 100,
					editable: true,
				},
				{
					headerName: '압력',
					colId: 'nickel102',
					field: 'nickel102',
					width: 100,
					valueFormatter: formatGridNumber2,
					editable: true,
				},
			],
		},
		{
			headerName: '니켈 2-1호기',
			headerClass: 'common-group-header',
			children: [
				{
					headerName: '교환여부',
					colId: 'nickel201',
					field: 'nickel201',
					width: 100,
					editable: true,
				},
				{
					headerName: '압력',
					colId: 'nickel202',
					field: 'nickel202',
					width: 100,
					valueFormatter: formatGridNumber2,
					editable: true,
				},
			],
		},
		{
			headerName: '유수분리기',
			headerClass: 'common-group-header',
			children: [
				{
					headerName: '교환여부',
					colId: 'oilWater01',
					field: 'oilWater01',
					width: 100,
					editable: true,
				},
				{
					headerName: '압력',
					colId: 'oilWater02',
					field: 'oilWater02',
					width: 100,
					valueFormatter: formatGridNumber2,
					editable: true,
				},
			],
		},
		{
			headerName: '비고',
			colId: 'remark',
			field: 'remark',
			width: 200,
			editable: true,
		},
	];
};

/**
 * 그룹 헤더 구조를 유지하면서 헤더 클래스 정규화
 */
const normalizeHeaderClass = (
	defs: (ColDef<EQM2060_R01Dto> | ColGroupDef<EQM2060_R01Dto>)[]
): (ColDef<EQM2060_R01Dto> | ColGroupDef<EQM2060_R01Dto>)[] => {
	return defs.map((def: any) => {
		// children이 있으면 재귀적으로 처리
		const children = Array.isArray(def.children) && def.children.length > 0
			? normalizeHeaderClass(def.children)
			: undefined;

		if (children) {
			// ColGroupDef인 경우 - 공통 그룹 헤더 클래스만 유지
			const groupHeaderClass = [def.headerClass, 'common-group-header']
				.filter(Boolean)
				.join(' ');
			return {
				...def,
				headerClass: groupHeaderClass,
				suppressMenu: true,
				children
			} as ColGroupDef<EQM2060_R01Dto>;
		} else {
			// ColDef인 경우 - 일반 헤더는 aggridHeaderClass 적용
			const mergedHeaderClass = [aggridHeaderClass, def.headerClass]
				.filter(Boolean)
				.join(' ');
			return {
				...def,
				headerClass: mergedHeaderClass,
				suppressMenu: true
			} as ColDef<EQM2060_R01Dto>;
		}
	}).filter(Boolean) as (ColDef<EQM2060_R01Dto> | ColGroupDef<EQM2060_R01Dto>)[];
};

/**
 * 최종 컬럼 정의 생성 (컬럼 상태 병합 포함)
 */
export const createColumnDefs = (
	baseColumnDefs: (ColDef<EQM2060_R01Dto> | ColGroupDef<EQM2060_R01Dto>)[],
	columnState: ColumnState[] | null
): (ColDef<EQM2060_R01Dto> | ColGroupDef<EQM2060_R01Dto>)[] => {
	// analysisDt 컬럼을 제거한 baseColumnDefs 사용
	// analysisDt 컬럼은 이미 baseColumnDefs에 포함되어 있으므로 필터링 불필요
	const filteredBaseColumnDefs = baseColumnDefs;

	const mergedColumnDefs = GridCustomColumnDefs<EQM2060_R01Dto>({
		baseColumnDefs: filteredBaseColumnDefs,
		columnState: columnState || null,
		headerNames,
		fallbackHeaderClass: aggridHeaderClass
	});

	return normalizeHeaderClass(mergedColumnDefs);
};

